import React, { useState, useEffect, useRef } from 'react';

import classNames from 'classnames';
import { Field } from 'react-final-form';

import { RangeSlider } from '../../../components';

import css from './IntegerRangeFilter.module.css';

const RADIX = 10;

//  Ensures that the given values object has both minimum and maximum values.
//  If `values` does not include `minValue` or `maxValue`, defaults are applied.
//  This is the "nothing selected" display - the full default range shown as
//  real values, e.g. on first load or after Reset.
const resolveMinMaxValues = (values, defaultMax, defaultMin) => {
  const { maxValue, minValue } = values || {};
  return {
    maxValue: maxValue != null ? maxValue : defaultMax,
    minValue: minValue != null ? minValue : defaultMin,
  };
};

const isTooSmall = (x, limit) => x < limit;
const isTooBig = (x, limit) => x > limit;
const isValidMin = (minValue, minLimit, maxValue) => {
  return (
    Number.isInteger(minValue) && !isTooSmall(minValue, minLimit) && !isTooBig(minValue, maxValue)
  );
};
const isValidMax = (maxValue, maxLimit, minValue) => {
  return (
    Number.isInteger(maxValue) && !isTooBig(maxValue, maxLimit) && !isTooSmall(maxValue, minValue)
  );
};

const getValidHandles = (currentValues, inputValues, minLimit, maxLimit) => {
  const isMinUpdated = currentValues.minValue !== inputValues.minValue;
  const isMaxUpdated = currentValues.maxValue !== inputValues.maxValue;

  if (isMinUpdated && isMaxUpdated) {
    const { minValue, maxValue } = inputValues;
    const bothAreIntegers = Number.isInteger(minValue) && Number.isInteger(maxValue);
    const inRange =
      bothAreIntegers && !(isTooSmall(minValue, minLimit) || isTooBig(maxValue, maxLimit));
    // If both values are changed and are within bounds, return the new values. Otherwise, return the default values.
    return inRange && minValue <= maxValue ? [minValue, maxValue] : [minLimit, maxLimit];
  }

  const { minValue, maxValue } = inputValues;
  const validMin = !isMinUpdated
    ? currentValues.minValue
    : !Number.isInteger(minValue)
    ? minLimit
    : isTooSmall(minValue, minLimit)
    ? minLimit
    : isTooBig(minValue, maxValue)
    ? maxValue
    : minValue;
  const validMax = !isMaxUpdated
    ? currentValues.maxValue
    : !Number.isInteger(maxValue)
    ? maxLimit
    : isTooBig(maxValue, maxLimit)
    ? maxLimit
    : isTooSmall(maxValue, minValue)
    ? minValue
    : maxValue;

  return [validMin, validMax];
};

const RangeInput = props => {
  const {
    input,
    min: defaultMinValue,
    max: defaultMaxValue,
    step,
    isInSideBar,
    initialValues,
    intl,
    getLabelForRangeInput,
  } = props;
  const { value: values = {}, onChange, name } = input;

  const currentValues = resolveMinMaxValues(initialValues[name], defaultMaxValue, defaultMinValue);
  const [fieldValues, setFieldValues] = useState(currentValues);

  // Tracks whether the user has explicitly cleared a side of the range by
  // typing/backspacing it to empty. While true, the sync effect below shows
  // that side as blank instead of re-filling it with the default limit - the
  // user should see an empty field ready for their own input, not have it
  // snap back to the full range. This is reset once the user types a real
  // value into that side again.
  const userClearedRef = useRef({ minValue: false, maxValue: false });

  // True while one of the two inputs has focus, i.e. while the user is actively
  // typing a value. The sync effect below is skipped for as long as that holds:
  // a live-edit filter commits through the URL, and a commit that lands
  // mid-edit would otherwise rewrite the half-typed value under the cursor.
  const isEditingRef = useRef(false);

  // initialValues is a brand-new object on every render (FilterComponent calls
  // a curried function that returns a fresh object each time, never memoized),
  // so depending on the object reference itself would re-run this effect on
  // every render - including ones triggered by this component's own
  // setFieldValues calls, e.g. while clearing the input with Backspace. That
  // stomps any in-progress edit that hasn't been committed via onChange yet
  // by snapping back to the last committed value. Depend on the actual
  // primitive values instead, so this only re-syncs when the committed min/max
  // genuinely change (Apply, URL change, Reset).
  useEffect(() => {
    // The committed value is the user's own keystrokes coming back around
    // through the URL, and it can lag behind what they have typed since. While
    // the field has focus their input wins; the pending submit commits it
    // moments later anyway, and blur re-opens this sync.
    if (isEditingRef.current) {
      return;
    }
    setFieldValues({
      minValue: userClearedRef.current.minValue ? '' : currentValues.minValue,
      maxValue: userClearedRef.current.maxValue ? '' : currentValues.maxValue,
    });
  }, [currentValues.minValue, currentValues.maxValue]);

  const handleFocus = () => {
    isEditingRef.current = true;
  };

  const handleMinValueChange = event => {
    const newValue = Number.parseInt(event.target.value, RADIX);

    // Faulty mode: new min value is not an integer (e.g. the field was
    // cleared). Commit the clear via onChange too, not just the local
    // display state - IntegerRangeFilter's live-edit submit is debounced
    // (see handleSubmitOnLive), so if this skipped onChange entirely, the
    // debounce would still have the last valid keystroke's value pending
    // and fire with that STALE value ~400ms later, silently resurrecting a
    // digit the user just deleted.
    if (!Number.isInteger(newValue)) {
      userClearedRef.current.minValue = true;
      const newValues = { ...fieldValues, minValue: '' };
      setFieldValues(newValues);
      onChange({ ...fieldValues, minValue: undefined });
      return;
    }

    // A real value is being entered again - the field is no longer in the
    // "explicitly cleared, stay blank" state.
    userClearedRef.current.minValue = false;

    // Faulty mode: new min value is greater than the current max value or
    //              new min value is less than the default min value
    if (newValue > fieldValues.maxValue || newValue < defaultMinValue) {
      const newValues = { ...fieldValues, minValue: newValue };
      setFieldValues(newValues);
      return;
    }

    const newValues =
      newValue < defaultMinValue
        ? { ...fieldValues, minValue: defaultMinValue }
        : { ...fieldValues, minValue: newValue };
    setFieldValues(newValues);
    onChange(newValues);
  };

  const handleMaxValueChange = event => {
    const newValue = Number.parseInt(event.target.value, RADIX);

    // Faulty mode: new max value is not an integer (e.g. the field was
    // cleared). Commit the clear via onChange too - see the matching
    // comment in handleMinValueChange for why this is necessary.
    if (!Number.isInteger(newValue)) {
      userClearedRef.current.maxValue = true;
      const newValues = { ...fieldValues, maxValue: '' };
      setFieldValues(newValues);
      onChange({ ...fieldValues, maxValue: undefined });
      return;
    }

    // A real value is being entered again - the field is no longer in the
    // "explicitly cleared, stay blank" state.
    userClearedRef.current.maxValue = false;

    // Faulty mode: new max value is less than the current min value or
    //              new max value is greater than the default max value
    if (newValue < fieldValues.minValue || newValue > defaultMaxValue) {
      const newValues = { ...fieldValues, maxValue: newValue };
      setFieldValues(newValues);
      return;
    }

    const newValues =
      newValue > defaultMaxValue
        ? { ...fieldValues, maxValue: defaultMaxValue }
        : { ...fieldValues, maxValue: newValue };
    setFieldValues(newValues);
    onChange(newValues);
  };

  const handleMinValueBlur = event => {
    isEditingRef.current = false;
    const inputValue = Number.parseInt(event.target.value, RADIX);
    if (
      !Number.isInteger(inputValue) ||
      inputValue < defaultMinValue ||
      inputValue > values.maxValue
    ) {
      // Committing a real value (the default) on blur - no longer "cleared".
      userClearedRef.current.minValue = false;
      const newValues = { ...fieldValues, minValue: defaultMinValue };
      setFieldValues(newValues);
      onChange(newValues);
    }
  };
  const handleMaxValueBlur = event => {
    isEditingRef.current = false;
    const inputValue = Number.parseInt(event.target.value, RADIX);
    if (
      !Number.isInteger(inputValue) ||
      inputValue < values.minValue ||
      inputValue > defaultMaxValue
    ) {
      // Committing a real value (the default) on blur - no longer "cleared".
      userClearedRef.current.maxValue = false;
      const newValues = { ...fieldValues, maxValue: defaultMaxValue };
      setFieldValues(newValues);
      onChange(newValues);
    }
  };

  const handleSliderChange = updatedValue => {
    // Dragging the slider sets real values for both sides.
    userClearedRef.current.minValue = false;
    userClearedRef.current.maxValue = false;
    isEditingRef.current = false;
    setFieldValues({ ...updatedValue });
    onChange({ ...updatedValue });
  };

  const labelForRangeInput = getLabelForRangeInput
    ? getLabelForRangeInput
    : (value, handleName) =>
        intl.formatMessage(
          { id: 'IntegerRangeFilter.screenreader.rangeHandle' },
          { value, handle: handleName }
        );

  const getHandleLabels = handles => {
    const multipleHandles = handles.length > 1;
    return handles.map((h, idx) => {
      const handleName =
        multipleHandles && idx === 0
          ? 'min'
          : multipleHandles && idx === handles.length - 1
          ? 'max'
          : 'handle';
      return labelForRangeInput(h, handleName);
    });
  };

  const isMinInvalid = !isValidMin(fieldValues.minValue, defaultMinValue, fieldValues.maxValue);
  const isMaxInvalid = !isValidMax(fieldValues.maxValue, defaultMaxValue, fieldValues.minValue);
  const classes = isInSideBar ? css.formWrapper : null;

  const validHandles = getValidHandles(values, fieldValues, defaultMinValue, defaultMaxValue);

  return (
    <div className={classes}>
      <div className={classNames(css.contentWrapper, { [css.contentWrapperSidebar]: isInSideBar })}>
        <div className={css.inputsWrapper}>
          {!isInSideBar ? (
            <span className={css.labelPopup}>
              {intl.formatMessage({ id: 'IntegerRangeFilter.rangeInputsLabel' })}
            </span>
          ) : null}
          <input
            className={classNames(css.minValue, {
              [css.valueInSidebar]: isInSideBar,
              [css.invalidInput]: isMinInvalid,
            })}
            type="number"
            name={`${name}_min`}
            min={defaultMinValue}
            max={defaultMaxValue}
            step={step}
            placeholder={defaultMinValue}
            value={fieldValues.minValue}
            onChange={handleMinValueChange}
            onFocus={handleFocus}
            onBlur={handleMinValueBlur}
            aria-valuenow={fieldValues.minValue}
            aria-valuetext={labelForRangeInput(fieldValues.minValue, 'min')}
            aria-valuemin={defaultMinValue}
            aria-valuemax={defaultMaxValue}
            aria-label={labelForRangeInput(fieldValues.minValue, 'min')}
          ></input>
          <span className={css.valueSeparator}>-</span>
          <input
            className={classNames(css.maxValue, {
              [css.valueInSidebar]: isInSideBar,
              [css.invalidInput]: isMaxInvalid,
            })}
            type="number"
            name={`${name}_max`}
            min={defaultMinValue}
            max={defaultMaxValue}
            placeholder={defaultMaxValue}
            step={step}
            value={fieldValues.maxValue}
            onChange={handleMaxValueChange}
            onFocus={handleFocus}
            onBlur={handleMaxValueBlur}
            aria-valuenow={fieldValues.maxValue}
            aria-valuetext={labelForRangeInput(fieldValues.maxValue, 'max')}
            aria-valuemin={defaultMinValue}
            aria-valuemax={defaultMaxValue}
            aria-label={labelForRangeInput(fieldValues.maxValue, 'max')}
          ></input>
        </div>
      </div>
      <div className={css.sliderWrapper}>
        <RangeSlider
          min={defaultMinValue}
          max={defaultMaxValue}
          ariaLabels={getHandleLabels(validHandles)}
          step={step}
          handles={validHandles}
          onChange={handles => {
            handleSliderChange({ minValue: handles[0], maxValue: handles[1] });
          }}
        />
      </div>
    </div>
  );
};

/**
 * FieldSelectIntegerRange component
 *
 * @component
 * @param {Object} props
 * @param {number} [props.min] - The minimum value
 * @param {number} [props.max] - The maximum value
 * @param {string} [props.name] - The name
 * @param {number} [props.step] - The step
 * @param {boolean} [props.isInSideBar] - Whether the filter is in the sidebar
 * @returns {JSX.Element}
 */
const FieldSelectIntegerRange = props => {
  const { max, min, name, step = 5, isInSideBar = false, ...rest } = props;
  return (
    <Field
      max={max}
      min={min}
      name={name}
      step={step}
      isInSideBar={isInSideBar}
      component={RangeInput}
      {...rest}
    />
  );
};

export default FieldSelectIntegerRange;
